import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PatientService} from '../../../patient/patient.service';
import {TdoDropdownModel} from '../../../../tdo-controls/common/tdo-basic-dropdown/models/tdo-dropdown.model';
import {TreatmentEndoDataModel} from '../../models/treatment-endo-data.model';
import {AnesthesiaConfigurationModel} from '../../models/configuration/anesthesia-configuration.model';
import {TreatmentService} from '../../../../services/treatment.service';
import {AnesthesiaEndoAutofillConfigurationModel} from '../../models/configuration/anesthesia-endo-autofill-configuration.model';
import {AnesthesiaEndoExplosionConfigurationModel} from '../../models/configuration/anesthesia-endo-explosion-configuration.model';
import {Globals} from '../../../../globals';
import {TreatmentEndoAnesthesiaDataModel} from '../../models/treatment-endo-anesthesia-data.model';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {EnumValidValueItemModel} from '../../../../models/enum/enum-valid-value-item.model';
import {StaticDataService} from '../../../../services/static-data.service';
import {DataPatientCaseWorkItemToothModel} from '../../../../models/teeth/data-patient-case-work-item-tooth.model';
import {DialogService} from '../../../../services/dialog.service';
import { AnesthesiaService } from 'src/app/services/anesthesia.service';
import { formatDate } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-treatment-anesthesia-dialog',
  templateUrl: './treatment-anesthesia-dialog.component.html',
  styleUrls: ['./treatment-anesthesia-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class TreatmentAnesthesiaDialogComponent implements OnInit, OnDestroy {
  isLoading = true; 
  patientInfo: string; 

  anesthesia: TreatmentEndoAnesthesiaDataModel[];
  treatmentModel: TreatmentEndoDataModel;
  configurationModel: AnesthesiaConfigurationModel;

  selectedAutoFillModel: AnesthesiaEndoAutofillConfigurationModel;
  selectedExplosionModel: AnesthesiaEndoExplosionConfigurationModel;

  anesthesiaQuantityEnums: EnumValidValueItemModel[];
  anesthesiaTypeEnums: EnumValidValueItemModel[];
  anesthesiaAdministeredEnums: EnumValidValueItemModel[];

  autoFillDropdownItems: TdoDropdownModel[] = [];
  explosionCodesDropdownItems: TdoDropdownModel[] = [];
  anesthesiaQuantityDropdownModels: TdoDropdownModel[] = [];
  anesthesiaTypeDropdownModels: TdoDropdownModel[] = [];
  anesthesiaAdministeredDropdownModels: TdoDropdownModel[] = [];
  isUserInput: boolean = false;

   patientCaseWorkItemTeeth: DataPatientCaseWorkItemToothModel[] = [];


  selectedRow: TreatmentEndoAnesthesiaDataModel;

  // Debouncing properties
  private saveQueueTimeout: any;
  private currentSaveSubscription: Subscription | null = null;
  private pendingSaveModel: TreatmentEndoAnesthesiaDataModel | null = null;
  private SAVE_DEBOUNCE_DELAY = 300;
  private readonly destroy$ = new Subject<void>();

  constructor(private dialogRef: MatDialogRef<TreatmentAnesthesiaDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: TreatmentEndoDataModel,
              private globals: Globals,
              private cd: ChangeDetectorRef,
              private dialogService: DialogService,
              private staticDataService: StaticDataService,
              private treatmentService: TreatmentService,
              private anesthesiaService: AnesthesiaService,
              private patientService: PatientService) {
    this.treatmentModel = data;
  }

  ngOnInit(): void {
    this.patientInfo = this.patientService.getPatientNameAndAge();

    this.anesthesiaQuantityEnums = this.staticDataService.getValidValueItemsByValidValueName('enumAnestQuantity');
    this.anesthesiaTypeEnums = this.staticDataService.getValidValueItemsByValidValueName('enumAnestType');
    this.anesthesiaAdministeredEnums = this.staticDataService.getValidValueItemsByValidValueName('enumAnestAnesthesias');

    this.anesthesiaAdministeredDropdownModels = this.getTdoDropdownDataSource(this.anesthesiaAdministeredEnums, this.anesthesiaAdministeredDropdownModels);
    this.anesthesiaQuantityDropdownModels = this.getTdoDropdownDataSource(this.anesthesiaQuantityEnums, this.anesthesiaQuantityDropdownModels);
    this.anesthesiaTypeDropdownModels = this.getTdoDropdownDataSource(this.anesthesiaTypeEnums, this.anesthesiaTypeDropdownModels);

    this.anesthesiaService.getAnesthesiaConfigurationModels().subscribe((configRes) => {
      this.configurationModel = configRes;

      for (const aModel of this.configurationModel.autoFillConfigs) {
        this.autoFillDropdownItems.push(new TdoDropdownModel(aModel.Name, aModel));
      }

      for (const eModel of this.configurationModel.explosionConfigs) {
        this.explosionCodesDropdownItems.push(new TdoDropdownModel(eModel.Name, eModel));
      }

      this.treatmentService.getAnesthesia(this.globals.currentPatientId).subscribe((dataRes) => {
        this.anesthesia = dataRes;
        this.isLoading = false;
        this.cd.markForCheck();

        // load patient teeth

        this.treatmentService.getAnesthesiaPatientTeeth(this.globals.currentPatientId).subscribe((dataRes) => {
          this.patientCaseWorkItemTeeth = dataRes;
        });


      });
    });
  }

  saveAnesthesiaItem(model: TreatmentEndoAnesthesiaDataModel, updateAfter = true) {
    this.queueSaveAnesthesiaItem(model, updateAfter);
  }

  private queueSaveAnesthesiaItem(model: TreatmentEndoAnesthesiaDataModel, updateAfter = true): void {
    if (this.saveQueueTimeout) {
      clearTimeout(this.saveQueueTimeout);
    }
    if (this.currentSaveSubscription) {
      this.currentSaveSubscription.unsubscribe();
      this.currentSaveSubscription = null;
    }
    
    // Store the model for potential immediate execution
    this.pendingSaveModel = model;
    
    this.saveQueueTimeout = setTimeout(() => {
      this.executeSaveAnesthesiaItem(model, updateAfter);
      this.pendingSaveModel = null; // Clear after execution
    }, this.SAVE_DEBOUNCE_DELAY);
  }

  private executeSaveAnesthesiaItem(model: TreatmentEndoAnesthesiaDataModel, updateAfter = true): void {
    this.currentSaveSubscription = this.treatmentService.saveAnesthesiaItem(model).subscribe((res) => {
      const lastModelIdx = this.anesthesia.findIndex(p => p.AnesthesiaID === model.AnesthesiaID);
      if (lastModelIdx > -1) {
        this.anesthesia[lastModelIdx] = res;
      } else {
        this.anesthesia.push(res);
      }
      this.cd.detectChanges();
    }, (error) => {
      console.error('Error saving anesthesia item:', error);
      this.currentSaveSubscription = null;
    });
  }

  onOkClick() {
    // Flush any pending debounced saves before closing
    this.flushPendingSaves();
    this.dialogRef.close();
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  /**
   * Flushes any pending debounced saves by executing them immediately.
   * This ensures that changes made to inputs are saved even if the user
   * clicks OK before the input loses focus.
   */
  private flushPendingSaves(): void {
    if (this.saveQueueTimeout && this.pendingSaveModel) {
      clearTimeout(this.saveQueueTimeout);
      this.saveQueueTimeout = null;
      // Execute the pending save immediately
      this.executeSaveAnesthesiaItem(this.pendingSaveModel, false);
      this.pendingSaveModel = null;
    }
  }

  onAddAutoFillClick() {
    const model = new TreatmentEndoAnesthesiaDataModel();
    const date = new Date().toLocaleString();
    model.PatientId = this.globals.currentPatientId;
    model.CaseWorkItemToothId = this.treatmentModel.CaseWorkItemTeeth[0].CaseWorkItemToothId;
    model.AnesthesiaDate = date;
    model.enumAnestAnesthesiaID = this.selectedAutoFillModel.enumAnestAnesthesiaID;
    model.enumAnestQuantityID = this.selectedAutoFillModel.enumAnestQuantityID;
    model.enumAnestTypeID = this.selectedAutoFillModel.enumAnestTypeID;
    this.saveAnesthesiaItem(model);
  }

  onAddN2OClick() {
    const model = new TreatmentEndoAnesthesiaDataModel();
    const date = new Date().toLocaleString();
    model.PatientId = this.globals.currentPatientId;
    model.CaseWorkItemToothId = this.treatmentModel.CaseWorkItemTeeth[0].CaseWorkItemToothId;
    model.AnesthesiaDate = date;
    model.isN2O = true;
    this.saveAnesthesiaItem(model);
  }

  onAddExplosionCodeClick() {
    for (const item of this.selectedExplosionModel.cfgAnesthesiaEndoExplosionItems) {
      const model = new TreatmentEndoAnesthesiaDataModel();
      const date = new Date().toLocaleString();
      model.PatientId = this.globals.currentPatientId;
      model.CaseWorkItemToothId = this.treatmentModel.CaseWorkItemTeeth[0].CaseWorkItemToothId;
      model.AnesthesiaDate = date;
      model.enumAnestAnesthesiaID = item.enumAnestAnesthesiaID;
      model.enumAnestQuantityID = item.enumAnestQuantityID;
      model.enumAnestTypeID = item.enumAnestTypeID;
      this.executeSaveAnesthesiaItem(model, false);
    }

  }

  onN2OChange(event: MatCheckboxChange, model: TreatmentEndoAnesthesiaDataModel) {
    model.isN2O = event.checked;
    this.saveAnesthesiaItem(model);
  }

  onDeleteRowClick(model: TreatmentEndoAnesthesiaDataModel) {
    this.dialogService.openConfirmDialog('Are you sure you want to delete this row?').subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.treatmentService.deleteAnesthesiaItem(model.AnesthesiaID).pipe(
          takeUntil(this.destroy$)
        ).subscribe((res) => {
          const modelIdx = this.anesthesia.findIndex(p => p.AnesthesiaID === model.AnesthesiaID);
          if (modelIdx > -1) {
            this.anesthesia.splice(modelIdx, 1);
            this.cd.detectChanges();
          }
        });
      }
    });
  }

  onAutoFillSelected(event: TdoDropdownModel) {
    this.selectedAutoFillModel = event.value as AnesthesiaEndoAutofillConfigurationModel;
  }

  onExplosionSelected(event: TdoDropdownModel) {
    this.selectedExplosionModel = event.value as AnesthesiaEndoExplosionConfigurationModel;
  }

  getToothNum(anesModel: any ) {   // TreatmentEndoAnesthesiaDataModel) {
    
    let toothModel = this.patientCaseWorkItemTeeth.find(p => p.CaseWorkItemToothId === anesModel.CaseWorkItemToothId);

    if(toothModel) {
      return toothModel.Notation;
    } else {
      return null;
    }


    
    //this logic is wroong
    //  musty be replaced wiith new logic 
    let model: DataPatientCaseWorkItemToothModel;
    if (this.treatmentModel.CaseWorkItemTeeth) {
      model = this.treatmentModel.CaseWorkItemTeeth.find(p => p.CaseWorkItemToothId === anesModel.CaseWorkItemToothId);
    }

    if (model) {
      const toothModel = this.treatmentModel.cfgTeeth.find(p => p.ToothId === model.ToothID);
      if (toothModel) {
        return toothModel.cfgToothDescriptions[0].Notation;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getTdoDropdownDataSource(enumItems: EnumValidValueItemModel[], dropdownModels: TdoDropdownModel[]) {
    dropdownModels = [];
    for (const enumItem of enumItems) {
      dropdownModels.push(new TdoDropdownModel(enumItem.ItemDisplayValue, enumItem));
    }
    return dropdownModels;
  }

  getTdoDropdownDataModels(enumItems: EnumValidValueItemModel[], dropdownModels: TdoDropdownModel[]) {
    dropdownModels = [];
    for (const enumItem of enumItems) {
      dropdownModels.push(new TdoDropdownModel(enumItem.ItemDisplayValue, enumItem));
    }
    return dropdownModels;
  }

  getDate(date: string) {
    if (date) {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString();
    } else {
      return null;
    }
  }

  getDateTime(date: string) {
    if (date) {
      const dateObj = new Date(date);
      return  formatDate(dateObj, 'M/d/yyyy hh:mm a', 'en_US'); //dateObj.toLocaleString('yyyy-MM-dd HH:mm');
    } else {
      return null;
    }
  }

  onAnesthesiaDateInputComplete(event: any, model: TreatmentEndoAnesthesiaDataModel) {
    const date = new Date(event);
    model.AnesthesiaDate = formatDate(date, 'M-d-yyyy HH:mm', 'en_US'); //date.toLocaleString();
    this.saveAnesthesiaItem(model);
  }

  onExpirationInputComplete(event: any, model: TreatmentEndoAnesthesiaDataModel) {
    const date = new Date(event);
    model.AnesthesiaExpDate = date.toLocaleString();
    this.saveAnesthesiaItem(model);
  }

  onBatchInputComplete(event: any, model: TreatmentEndoAnesthesiaDataModel) {
    model.AnesthesiaBatch = event;
    this.saveAnesthesiaItem(model);
  }

  getDropdownValueById(enumAnestQuantityID: string, dropDownModels: TdoDropdownModel[]) {
    if (enumAnestQuantityID && dropDownModels) {
      const model = dropDownModels.find(p => p.value?.ValidValueItemId === enumAnestQuantityID);
      if (model) {
        return model;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }


  setUserInput(event: boolean) {
    this.isUserInput = event;
  }
  onDropdownItemSelected(event: EnumValidValueItemModel, enumId: string, model: TreatmentEndoAnesthesiaDataModel) {
    // Skip if not triggered by user
    if (!this.isUserInput) {
      return;
    }
    
    model[enumId] = event.ValidValueItemId;
    if (!model.AnesthesiaDate || model.AnesthesiaDate.length === 0) {
      model.AnesthesiaDate = new Date().toLocaleString();
    }
    this.saveAnesthesiaItem(model);
  }

  getBatch(val: string) {
    if (val) {
      return val;
    } else {
      return null;
    }
  }

  onTimeInputFocus(model: TreatmentEndoAnesthesiaDataModel, property: string) {

    if (!model.AnesthesiaDate || model.AnesthesiaDate.length === 0) {
      model.AnesthesiaDate = new Date().toLocaleString();
    }

    if(model[property] === null || model[property] === '') {
      model[property] = new Date().toLocaleTimeString();
      this.saveAnesthesiaItem(model, true);
    }

    console.log("onTimeInputFocus", model[property]);
  }

  onAddAnesthesiaRow() {
    const model = new TreatmentEndoAnesthesiaDataModel();
    model.PatientId = this.globals.currentPatientId;
    model.CaseWorkItemToothId = this.treatmentModel.CaseWorkItemTeeth[0].CaseWorkItemToothId;
    this.treatmentService.saveAnesthesiaItem(model).pipe(
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.anesthesia.push(res);
      this.cd.detectChanges();
    });
  }

  onRowSelected(model: TreatmentEndoAnesthesiaDataModel) {
    this.selectedRow = model;
  }

  isSelectedRow(model: TreatmentEndoAnesthesiaDataModel) {
    return this.selectedRow === model;
  }

  onExpirationDateClick(model: TreatmentEndoAnesthesiaDataModel) {
    if (!model.AnesthesiaExpDate || model.AnesthesiaExpDate === '') {
      model.AnesthesiaExpDate = new Date().toLocaleDateString();
      this.saveAnesthesiaItem(model);
    }
  }
  onAnesthesiaDateClick(model: TreatmentEndoAnesthesiaDataModel) {
    if (!model.AnesthesiaDate || model.AnesthesiaDate === '') {
      model.AnesthesiaDate = formatDate(new Date(), 'M-d-yyyy HH:mm', 'en_US'); //new Date().toLocaleString('yyyy-MM-dd HH:mm');
      this.saveAnesthesiaItem(model);
    }
  }
  onInputFocusTimeInsert(model: TreatmentEndoAnesthesiaDataModel, property: string, event: any) {
    if (!model.AnesthesiaDate || model.AnesthesiaDate.length === 0) {
      model.AnesthesiaDate = new Date().toLocaleString();
    }

    let d= model.AnesthesiaDate;
    let t= event;

    if(t==='') {
      model[property] ="";

      this.saveAnesthesiaItem(model, true);
      return;

    }
   
    // Create datetime variable by combining date portion of d and time portion of t
    const dateOnly = new Date(d).toDateString(); // Extract date portion
    const timeOnly = t || new Date().toLocaleTimeString(); // Use time from t or current time as fallback
    const combinedDateTime = new Date(`${dateOnly} ${timeOnly}`);
    model[property] = formatDate(combinedDateTime, 'M-d-yyyy HH:mm:ss', 'en_US');

    this.saveAnesthesiaItem(model, true);
  }

  onN2OPercentChange(event: string, model: TreatmentEndoAnesthesiaDataModel) {
    model.N2OxPctN2O = parseInt(event) || 0;
    this.saveAnesthesiaItem(model);
  }

  getTime(time: string) {
    if (time !== null){
      let result = new Date(time).toLocaleTimeString();
      return result === 'Invalid Date' ? time : result;
    }
    return "";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up queuing system resources
    if (this.saveQueueTimeout) {
      clearTimeout(this.saveQueueTimeout);
      this.saveQueueTimeout = null;
    }
    if (this.currentSaveSubscription) {
      this.currentSaveSubscription.unsubscribe();
      this.currentSaveSubscription = null;
    }
  }
}
